import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mergeMap, of, Subject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { GetAllAccountPointsService } from '../get-all-account-points.service';
import {
  getAllAccountPointsAction,
  getAllAccountPointsBulkAssignAccountsAction,
  getAllAccountPointsBulkAssignCategoryAction,
  getAllAccountPointsBulkAssignTagsAction,
  getAllAccountPointsBulkRemoveAccountsAction,
  getAllAccountPointsBulkRemoveTagsAction,
  getAllAccountPointsBulkUpdateAccountsTypeAction,
  getAllAccountPointsFailureAction,
  getAllAccountPointsSuccessAction
} from './get-all-account-points.actions';
import { assignTagsSuccessAction } from '../../../bulk-operations/tags/assign-tags/store/assign-tags.actions';
import { removeTagsSuccessAction } from '../../../bulk-operations/tags/remove-tags/store/remove-tags.actions';
import { assignCategorySuccessAction } from '../../../bulk-operations/categories/assign-category/store/assign-category.actions';
import { updateAccountsTypeSuccessAction } from '../../../bulk-operations/accounts/update-accounts-type/store/update-accounts-type.actions';
import { assignAccountsSuccessAction } from '../../../bulk-operations/accounts/assign-accounts/store/assign-accounts.actions';
import { removeAccountsSuccessAction } from '../../../bulk-operations/accounts/remove-accounts/store/remove-accounts.actions';
import { GetAllAccountPointsContext } from './get-all-account-points.types';

const requestsState = new Map<GetAllAccountPointsContext, Subject<void>>();

@Injectable()
export class GetAllAccountPointsEffects {
  getAllAccountPoints$ = createEffect(() => this.actions$.pipe(
    ofType(getAllAccountPointsAction),
    mergeMap(action => {
      const context: GetAllAccountPointsContext = action.context ?? 'default';
      const pending = requestsState.get(context);
      if (pending) {
        pending.next();
        pending.complete();
      }
      const cancelRequest = new Subject<void>();
      requestsState.set(context, cancelRequest);
      return this.getAllAccountPointsService.load(action.params, action.refresh, action.context).pipe(
        map(accountPoints => getAllAccountPointsSuccessAction({ accountPoints, context: action.context })),
        catchError(error => of(getAllAccountPointsFailureAction({ error, context: action.context }))),
        takeUntil(cancelRequest),
      );
    }),
  ));

  getAllAccountPointsBulkAssignTagsSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(assignTagsSuccessAction),
    map(action =>
      getAllAccountPointsBulkAssignTagsAction({ payload: action.data })
    )
  ));

  getAllAccountPointsBulkRemoveTagsSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(removeTagsSuccessAction),
    map(action =>
      getAllAccountPointsBulkRemoveTagsAction({ payload: action.data })
    )
  ));

  getAllAccountPointsBulkAssignCategorySuccess$ = createEffect(() => this.actions$.pipe(
    ofType(assignCategorySuccessAction),
    map(action =>
      getAllAccountPointsBulkAssignCategoryAction({ payload: action.data })
    )
  ));

  getAllAccountPointsBulkUpdateAccountsTypeSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(updateAccountsTypeSuccessAction),
    map(action =>
      getAllAccountPointsBulkUpdateAccountsTypeAction({ payload: action.data })
    )
  ));

  getAllAccountPointsBulkAssignAccountsSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(assignAccountsSuccessAction),
    map(action =>
      getAllAccountPointsBulkAssignAccountsAction({ payload: action.data })
    )
  ));

  getAllAccountPointsBulkRemoveAccountsSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(removeAccountsSuccessAction),
    map(action =>
      getAllAccountPointsBulkRemoveAccountsAction({ payload: action.data })
    )
  ));

  constructor(
    private actions$: Actions,
    private getAllAccountPointsService: GetAllAccountPointsService,
  ) {
  }
}
